import { useRef, useEffect, useState, useCallback } from "react";
import { Plus, RotateCw, SendHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ExpressionPicker } from "@/components/blocks/expression-picker";
import { ExpressionGifPicker } from "@/components/blocks/expression-gif-picker";
import { ImageUpload } from "@/components/blocks/image-upload";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { uploadToImgBB } from "@/lib/imgbb-api";
import { uploadImage } from "@/lib/r2-upload";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";

interface MessageInputProps {
  maxHeight?: number;
  className?: string;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  onSend: () => void;
  onSendDirect: (value: string) => void;
  locale?: string;
}

export function MessageInput({
  maxHeight = 120,
  className,
  message,
  setMessage,
  onSend,
  onSendDirect,
  locale = "en",
}: MessageInputProps) {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [isPasteProcessing, setIsPasteProcessing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleUploadImage = async (
    file: File,
    service: "imgbb" | "r2",
    resizeOption: string,
  ) => {
    setIsUploading(true);

    try {
      let url: string;

      if (service === "r2") {
        // R2 업로드 (로그인 필요, 토큰 사용)
        if (!user?.token) {
          console.error("R2 upload requires authentication");
          return;
        }
        url = await uploadImage(file, user.token);
      } else {
        // ImgBB 업로드 (리사이징 적용)
        const size = parseInt(resizeOption);
        url = await uploadToImgBB(file, size, size);
      }

      onSendDirect(url);
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSend = () => {
    if (message.trim()) {
      onSend();
    }
  };

  const handleSelect = (select: { type: string; value: string }) => {
    if (select.type === "Emoji") {
      setMessage((prev) => prev + select.value);
    } else if (select.type === "Sticker") {
      // TODO: Sticker
    } else if (select.type === "GIF") {
      onSendDirect(select.value);
    } else {
      console.error("Unknown Expression Type:", select);
    }
  };

  // 클립보드 이미지 처리 함수 (기본 ImgBB, 1024px)
  const handlePasteImage = useCallback(
    async (e: ClipboardEvent) => {
      if (isUploading || isPasteProcessing) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          setIsPasteProcessing(true);
          const file = items[i].getAsFile();
          if (file) {
            try {
              // await handleUploadImage(file, "imgbb", "1024");
              await handleUploadImage(file, "r2", "0");
            } catch (error) {
              console.error("이미지 업로드 실패:", error);
            } finally {
              setIsPasteProcessing(false);
            }
          }
          break;
        }
      }
    },
    [isUploading, isPasteProcessing],
  );

  useEffect(() => {
    window.addEventListener("paste", handlePasteImage);
    return () => window.removeEventListener("paste", handlePasteImage);
  }, [handlePasteImage]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isUploading) {
        handleSend();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      // 초기화.
      textarea.style.height = "40px";
      textarea.style.maxHeight = `${maxHeight}px`;
      // 높이 계산.
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [message, maxHeight]);

  return (
    <div className={cn("flex w-full items-end p-0 rounded-lg", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon">
            <Plus className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex w-fit p-0" side="top" align="start">
          <ExpressionPicker onSelect={handleSelect} />
          <ExpressionGifPicker onSelect={handleSelect} />
          <ImageUpload onUpload={handleUploadImage} locale={locale} />
        </PopoverContent>
      </Popover>
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Type a message..."
        className="flex-grow h-10 min-h-[40px] py-2 px-1 border-1 bg-transparent focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none resize-none overflow-y-auto"
      />
      <div className="flex">
        <Button
          variant="ghost"
          onClick={handleSend}
          disabled={isUploading || isPasteProcessing}
        >
          {isUploading || isPasteProcessing ? (
            <RotateCw className="h-4 w-4 animate-spin" />
          ) : (
            <SendHorizontal className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
